import { useCallback, useReducer } from "react";
import { BundleTypes } from "../constants";
import { FBTConfigInterface, Action, ChangeEvents } from "../Types";

export const initialConfigData: FBTConfigInterface = {
    status: false,
    bundle_type: BundleTypes.FBT,
    bundle_as_product: {
        product_title: "",
        product_description: "",
        images: [],
        show_on_product_page: [],
        product_handle: "",
        double_discount: false,
        collections: [],
    },
    isProductPageVisible: false,
    // design from
    bundles_design: {
        default_cta: false,
        bundle_title_size: 22,
        bundle_title_color: "#000000",
        bundle_title_style: "450 normal",
        product_name_size: 14,
        product_name_color: "#050505",
        product_name_style: "450 normal",
        product_price_size: 10,
        product_price_color: "#050505",
        product_price_style: "350 normal",
        border_color: "#050505",
        border_width: 2,
        border_radius: 10,
        background_color: "#ffffff",
        highlighted_offer_tag_size: 12,
        highlighted_offer_tag_color: "#F8F8F8",
        highlighted_offer_tag_style: "550 italic",
        highlighted_price_tag_size: 14,
        highlighted_price_tag_color: "#050505",
        highlighted_price_tag_style: "750 normal",
        total_footer_text_size: 14,
        total_footer_text_color: "#000000",
        total_footer_text_style: "550 normal",
        total_footer_price_text_size: 16,
        total_footer_price_text_color: "#000000",
        total_footer_price_text_style: "750 normal",
        total_footer_background_color: "#F5F5F5",
        total_footer_background_style: "450 normal",
        product_background_color: "#FFFFFF",
        product_border_color: "#000000",
        add_to_cart_bg_color: "#303030",
        add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#FFFFFF",
        add_to_cart_border_width: 0,
        add_to_cart_text_color: "#F5F5F5",
        add_to_cart_text_size: 14,
        add_to_cart_text_style: "750 normal",
        bundle_widget_bg: "#ffffff",
        bundle_widget_font_family: "Custom Store Font",
        product_img_size: "small",
        product_img_show: "all",
        product_img_border_radius: 10,
        product_img_border_width: 1,
        product_img_border_color: "#cccccc",
        variant_selector_text_size: 12,
        variant_selector_text_color: "#383838",
        variant_selector_text_style: "450 normal",
        swatches_select_color: "#000000",
    },
    allowed_product_pages: [],
    render_product_pages_type: false,
    // content form
    bundle_name: "",
    title: "Frequently Bought Together",
    description: "",
    discount_type: "percentage",
    discount_value: 5,
    order_discounts: true,
    product_discounts: true,
    shipping_discounts: true,
    fbt_product_selection: "manual",
    fbt_show_checkboxes: 1,
    ai_fbt_product_limit: 2,
    show_ai_fbt_for: "every",
    ai_fbt_allowed_products: [],
    products: [],
    volume_discounts: [],
    discounted_products: [],
    offered_products: [],
    bundle_product_level: "product",
    hide_native_elements: "",
    add_to_cart_cta_text: "Add Bundle to Cart",
    footer_total_text: "Total",
    footer_text: "Off",
    free_text: "Free",
    start_date: new Date().toISOString(),
    end_date: null,
    use_compare_at_price: false,
    prevent_deselect_main_product: false,
    no_preselect_offered_products: false,
    show_swatches: false,
    // @ts-expect-error hack
    selected_template_fbt: "", // by default, it will be empty, after user selects, it gets filled
    swatchOptions: [],
    swatchesProductsMap: {},
    swatchShape: "circle",
    swatchSize: 18,
    apply_discount_on_compare_price: false,
    offer_name: "",
    discount_label_template: "Frequently Bought Together",
    // NEW KEY: tracks whether manual mode uses "all" or "specific" products
    manual_product_scope: "specific" as "all" | "specific",

    // target from
    targeting: {
        includeCustomers: "all",
        excludeCustomers: "none",
        includeCustomerData: "",
        excludeCustomerData: "",
        shopifyMarketsTargeting: "all",
        includeLocations: [],
        excludeLocations: [],
        shopifyMarketsTargetingData: [],
        //newKeys
        targetingType: "all",
        customerTagsEnabled: false,
        customerTagType: "include",
        customerTags: "",
        customerSpentEnabled: false,
        customerSpentType: "greater_than",
        customerSpent: "",
        customerCountriesEnabled: false,
        customerCountryType: "include",
        customerCountries: [],
    },

    // Settings
    general_settings: {
        show_bundle_on_product_page: false,
        show_multiple_bundles_on_product_page: false,
        redirect_after_adding_product_to_cart: "",
        redirect_to_checkout: false,
        show_powered_by_kefi: true,
        max_width: 1000,
        override_max_width: false,
        override_margin_top: false,
        margin_top: 20,
    },
    bundles_settings: {
        enable_stock_control: false,
        out_of_stock_text: "Out of stock",
        listing_variant_with_image: false,
        work_only_with_widget: false,
        bundle_position: "fbt_default",
        custom_selector: "",
    },
    show_bundle_widget: "show_everywhere",
    free_shipping: false,
    animation_add_to_cart_btn: "none",
    new_bundle_position: "fbt_default",
    new_custom_selector: "",
};

const useFBTHook = () => {
    const bundleConfigReducer = useCallback(
        (state: FBTConfigInterface, action: Action<FBTConfigInterface>): FBTConfigInterface => {
            switch (action?.type) {
                case ChangeEvents.initState:
                    return { ...state, ...action.init };
                default:
                    if (action?.key && action.isComplex) {
                        return {
                            ...state,
                            [action?.key]: {
                                ...(state[action.key] as object),
                                ...(action.payload as object),
                            },
                        };
                    } else if (action?.key) return { ...state, [action?.key]: action.payload };
                    else return { ...state };
            }
        },
        [],
    );

    const [bundleConfig, dispatchBundleConfig] = useReducer(bundleConfigReducer, initialConfigData);

    return {
        bundleConfig,
        dispatchBundleConfig,
    };
};

export default useFBTHook;
